/**
 * Ce qu'on dit au joueur en sortant d'un comptoir, et ce qu'on enregistre.
 *
 * Le message est cosmétique ; « a-t-il gagné » ne l'est pas — c'est cette réponse qui
 * ouvre le comptoir suivant. La calculer dans un composant reviendrait à ne jamais
 * la tester.
 *
 * ⚠️ **Place à la table, et non siège.** Le tournoi désigne celui qui paie par sa
 * place à la table (`Tournoi.paye`), pas par son siège dans la cave en cours : en
 * cave des caves il n'y a que deux sièges, et le siège 0 y désigne quelqu'un
 * d'autre. Le joueur est toujours la **place 0**, parce que `placesDuComptoir`
 * l'assied en premier. Confondre les deux ferait payer sa tournée à un vainqueur.
 */

import { defiReussi, type Comptoir, type Statistiques, type Tournoi } from "@zinc421/shared";

import type { Textes } from "../i18n/index.js";
import { libelleDefi, nomDeLEtape } from "../jeu/libelles.js";

/** La place du joueur humain à la table d'un comptoir. */
export const MA_PLACE = 0;

export interface Issue {
  readonly gagne: boolean;
  readonly etoile: boolean;
  readonly message: string;
}

export function issueDeComptoir(
  comptoir: Comptoir, paye: number | null, stats: Statistiques, textes: Textes,
): Issue {
  const gagne = paye !== null && paye !== MA_PLACE;
  const etoile = defiReussi(comptoir.defi, { gagne, stats });
  const lieu = textes.comptoirNom(comptoir.cle);

  if (!gagne) {
    return {
      gagne, etoile: false,
      message: textes.issuePerdu(textes.auComptoir(lieu)),
    };
  }
  return {
    gagne, etoile,
    message: etoile
      ? textes.issueGagneEtoile(lieu)
      : textes.issueGagne(lieu, libelleDefi(comptoir.defi, textes)),
  };
}

/**
 * Le titre affiché en tête de l'écran de partie.
 *
 * Sur un comptoir d'une seule cave, le lieu suffit — annoncer « La cave » n'apprend
 * rien puisqu'il n'y en aura pas d'autre. En soirée, il faut au contraire savoir si
 * l'on joue la cave, la recave ou la finale.
 */
export function titreDeCave(
  comptoir: Comptoir, tournoi: Tournoi, textes: Textes,
): string {
  const lieu = textes.comptoirNom(comptoir.cle);
  if (tournoi.format === "SIMPLE") return lieu;
  return `${lieu} — ${nomDeLEtape(tournoi.etape, textes)}`;
}

/**
 * Ce que propose le bouton de fin de cave.
 *
 * Déduit de l'étape COURANTE, sans appeler `caveSuivante` : celle-ci lève tant que la
 * cave n'a pas de perdant, et le libellé se calcule à chaque rendu.
 */
export function libelleSuiteComptoir(tournoi: Tournoi, textes: Textes): string {
  if (tournoi.format === "SIMPLE" || tournoi.etape === "FINI") {
    return textes.retourALaTournee;
  }
  switch (tournoi.etape) {
    case "CAVE": return textes.enchainerLaRecave;
    // Après la recave, la finale n'a lieu que si les deux perdants diffèrent : on ne
    // promet donc pas une cave des caves qui pourrait ne pas se jouer.
    case "RECAVE": return textes.laSuite;
    default: return textes.voirQuiPaie;
  }
}
